import axios from 'axios';
import { BASE_URL } from '../../../constants/api';
import { FetchResult } from '../../type/apiType';

const DEFAULT_FETCH_RESULT: FetchResult<any> = {
  count: 0,
  next: null,
  previous: null,
  page_next: null,
  page_previous: null,
  page_size: 0,
  results: [],
};

export class ApiService<T> {
  constructor(public apiPath: string) {}

  public async create(item: T): Promise<T> {
    const response = await axios.post(this.constructUrl(), item);
    return response.data;
  }

  public async createByUrl<Req = unknown>(apiPath: string, body: Req): Promise<void> {
    await axios.post(this.constructUrl(undefined, apiPath), body);
  }

  public async deleteByUrl<Req = unknown>(apiPath: string, body: Req): Promise<void> {
    await axios.delete(this.constructUrl(undefined, apiPath), { data: body });
  }

  public async update(id: number, data: Partial<T>): Promise<T> {
    const response = await axios.patch(this.constructUrl(id), data);
    return response.data;
  }

  public async updateByPut(id: number, data: Partial<T>): Promise<void> {
    const response = await axios.put(this.constructUrl(id), data);
    return response.data;
  }

  public async updateBySubPath<Req = unknown>(subPath: string, data: Req): Promise<void> {
    await axios.put(this.constructUrl(undefined, subPath), data);
  }

  public async delete(id: number): Promise<void> {
    await axios.delete(this.constructUrl(id));
  }

  public async fetch(queryParams?: URLSearchParams): Promise<FetchResult<T>> {
    const response = await axios.get(this.constructUrl(), { params: queryParams });
    return response.data;
  }

  public async fetchById(id: number, queryParams?: Record<string, string>): Promise<T> {
    const response = await axios.get(this.constructUrl(id), { params: queryParams });
    return response.data;
  }

  public async fetchByIds(
    ids: number[],
    queryParams?: Record<string, string>
  ): Promise<FetchResult<T>> {
    if (ids.length === 0) {
      return DEFAULT_FETCH_RESULT;
    }
    const response = await axios.get(this.constructUrl(), {
      params: { ids: ids.join(','), ...queryParams },
    });
    return response.data;
  }

  public async fetchBySubPath<T>(
    subPath: string,
    queryParams?: URLSearchParams
  ): Promise<FetchResult<T>> {
    const response = await axios.get(this.constructUrl(undefined, subPath), {
      params: queryParams,
    });
    return response.data;
  }

  public async fetchBySubPathRaw<T>(subPath: string, queryParams?: URLSearchParams): Promise<T> {
    const response = await axios.get(this.constructUrl(undefined, subPath), {
      params: queryParams,
    });
    return response.data;
  }

  public async createByFormData(formData: FormData): Promise<T> {
    const response = await axios.post(this.constructUrl(), formData);
    return response.data;
  }

  public async updateByFormData(id: number, formData: FormData): Promise<T> {
    const response = await axios.patch(this.constructUrl(id), formData);
    return response.data;
  }

  public async fetchChildItemsByParentId<T>(
    parentId: number,
    subPath: string = '/',
    queryParams?: URLSearchParams
  ): Promise<T> {
    const response = await axios.get(this.constructUrl(parentId, subPath), { params: queryParams });
    return response.data;
  }

  public async fetchItemByParentIdAndItemId<T>(
    parentId: number,
    itemId: number,
    subPath: string = '/',
    queryParams?: URLSearchParams
  ): Promise<T> {
    const response = await axios.get(this.constructUrl(parentId, subPath, itemId), {
      params: queryParams,
    });
    return response.data;
  }

  public async createItemsForParent(
    parentId: number,
    subPath: string = '/',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    requestBody?: Record<string, any>,
    queryParams?: URLSearchParams
  ): Promise<void> {
    await axios.post(this.constructUrl(parentId, subPath), requestBody, { params: queryParams });
  }

  public async updateItemsForParent(
    parentId: number,
    subPath: string = '/',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    requestBody?: Record<string, any>,
    queryParams?: URLSearchParams
  ): Promise<void> {
    await axios.patch(this.constructUrl(parentId, subPath), requestBody, { params: queryParams });
  }

  public async deleteItemsForParent(
    parentId: number,
    subPath: string = '/',
    queryParams?: URLSearchParams
  ): Promise<void> {
    await axios.delete(this.constructUrl(parentId, subPath), {
      params: queryParams,
    });
  }

  public async createBySubPath<Res = T, Req = unknown>(subPath: string, data: Req): Promise<Res> {
    const response = await axios.post(this.constructUrl(undefined, subPath), data);
    return response.data;
  }

  protected constructUrl(id?: number, subPath: string = '/', childId?: number): string {
    let url = `${BASE_URL}${this.apiPath}`;

    if (id !== undefined) {
      url += `${id}/`;
    }

    if (subPath) {
      url += subPath.startsWith('/') ? subPath.slice(1) : subPath;
      if (!url.endsWith('/')) {
        url += '/';
      }
    }

    if (childId !== undefined) {
      url += `${childId}/`;
    }

    return url;
  }
}
